import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Bot, Cpu, Key, RefreshCw, Loader2, CheckCircle, 
  Database, Sparkles, AlertTriangle, Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIConfigPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [availableModels, setAvailableModels] = useState({});
  
  // Form state
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('gpt-4o-mini');
  const [useEmergentKey, setUseEmergentKey] = useState(true);
  const [feedbackEnabled, setFeedbackEnabled] = useState(true);
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [googleKey, setGoogleKey] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/admin/professions/ai/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const cfg = response.data.config;
      setConfig(cfg);
      setStats(response.data.feedback_stats);
      setAvailableModels(response.data.available_models || {});
      
      // Set form values
      setProvider(cfg.provider || 'openai');
      setModel(cfg.model || 'gpt-4o-mini');
      setUseEmergentKey(cfg.use_emergent_key !== false);
      setFeedbackEnabled(cfg.feedback_enabled !== false);
    } catch (error) {
      console.error('Error loading AI config:', error);
      toast.error('Error al cargar configuración de IA');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      
      const params = new URLSearchParams();
      params.append('provider', provider);
      params.append('model', model);
      params.append('use_emergent_key', useEmergentKey);
      params.append('feedback_enabled', feedbackEnabled);
      
      if (openaiKey) params.append('openai_key', openaiKey);
      if (anthropicKey) params.append('anthropic_key', anthropicKey);
      if (googleKey) params.append('google_key', googleKey);
      
      await axios.put(`${API}/admin/professions/ai/config?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Configuración de IA guardada');
      setOpenaiKey('');
      setAnthropicKey('');
      setGoogleKey('');
      await loadConfig();
    } catch (error) {
      console.error('Error saving AI config:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const clearFeedbackCache = async () => {
    if (!window.confirm('¿Estás seguro? Esto eliminará todo el feedback cacheado y los próximos exámenes generarán feedback nuevo (con coste).')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.delete(`${API}/admin/professions/ai/feedback-cache`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      await loadConfig();
    } catch (error) {
      toast.error('Error al limpiar caché');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bot className="w-6 h-6 text-indigo-500" />
            Configuración de IA
          </h2>
          <p className="text-slate-600 mt-1">
            Gestiona el proveedor de IA y el caché de feedback
          </p>
        </div>
        <Button onClick={loadConfig} variant="outline" className="rounded-full">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <Database className="w-6 h-6 text-emerald-500 mb-2" />
            <p className="text-2xl font-bold text-emerald-700">{stats.total_cached_feedback}</p>
            <p className="text-sm text-emerald-600">Feedback Cacheado</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <Sparkles className="w-6 h-6 text-blue-500 mb-2" />
            <p className="text-2xl font-bold text-blue-700">{stats.total_cache_hits}</p>
            <p className="text-sm text-blue-600">Usos del Caché</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <CheckCircle className="w-6 h-6 text-amber-500 mb-2" />
            <p className="text-2xl font-bold text-amber-700">{stats.estimated_savings}</p>
            <p className="text-sm text-amber-600">Llamadas API Ahorradas</p>
          </div>
        </div>
      )}

      {/* Provider Selection */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
          <Cpu className="w-5 h-5 text-indigo-500" />
          Proveedor y Modelo
        </h3>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Proveedor</label>
            <select
              value={provider}
              onChange={(e) => {
                setProvider(e.target.value);
                // Reset model when provider changes
                const models = availableModels[e.target.value] || [];
                setModel(models[0] || '');
              }}
              className="w-full p-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="google">Google (Gemini)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Modelo</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full p-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500"
            >
              {(availableModels[provider] || []).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-6 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useEmergentKey}
              onChange={(e) => setUseEmergentKey(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-slate-700">Usar Emergent Universal Key (fallback)</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={feedbackEnabled}
              onChange={(e) => setFeedbackEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-slate-700">Feedback IA Habilitado</span>
          </label>
        </div>

        {!useEmergentKey && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Configura tu propia API Key para el proveedor seleccionado
            </p>
          </div>
        )}
      </div>

      {/* API Keys */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-indigo-500" />
          API Keys (opcionales)
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Configura tus propias API keys si no quieres usar la Emergent Universal Key
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              OpenAI API Key 
              <span className="text-slate-400 ml-2">
                {config?.api_keys?.openai !== 'No configurada' ? `(${config?.api_keys?.openai})` : '(No configurada)'}
              </span>
            </label>
            <Input
              type="password"
              placeholder="sk-..."
              value={openaiKey}
              onChange={(e) => setOpenaiKey(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Anthropic API Key
              <span className="text-slate-400 ml-2">
                {config?.api_keys?.anthropic !== 'No configurada' ? `(${config?.api_keys?.anthropic})` : '(No configurada)'}
              </span>
            </label>
            <Input
              type="password"
              placeholder="sk-ant-..."
              value={anthropicKey}
              onChange={(e) => setAnthropicKey(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Google API Key
              <span className="text-slate-400 ml-2">
                {config?.api_keys?.google !== 'No configurada' ? `(${config?.api_keys?.google})` : '(No configurada)'}
              </span>
            </label>
            <Input
              type="password"
              placeholder="AIza..."
              value={googleKey}
              onChange={(e) => setGoogleKey(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Cache Management */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-indigo-500" />
          Gestión del Caché de Feedback
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          El feedback se guarda por pregunta. Si cambias de proveedor/modelo, puedes limpiar el caché para regenerar con el nuevo modelo.
        </p>
        
        <Button 
          onClick={clearFeedbackCache}
          variant="outline"
          className="text-red-600 border-red-200 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Limpiar Caché de Feedback
        </Button>
      </div>

      {/* Save Button */}
      <Button 
        onClick={saveConfig}
        disabled={saving}
        className="w-full py-6 rounded-xl bg-indigo-600 hover:bg-indigo-700"
      >
        {saving ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
        ) : (
          <><CheckCircle className="w-4 h-4 mr-2" /> Guardar Configuración</>
        )}
      </Button>
    </div>
  );
};

export default AIConfigPanel;
